// Location *corroboration* — the one JS-side home for the logic the console SPA
// and the api both need. Do the candidate's independent location signals — the
// coarse region their device reported at verify time, the country their phone is
// in, the location they state on their profile, and the locations the connected
// identity profiles list (GitHub) — agree? Signals that line up are weak-but-real
// corroboration; ones that confidently conflict are worth a human glance.
//
// Deliberately **conservative and never a block** (mirrors the name-match check in
// `profileCorroboration`): location is trivially maskable (VPN, travel, "Remote"
// on a résumé), so we only raise `fail` when two signals *confidently* disagree,
// treat free text we can't confidently parse as "no claim", and otherwise surface
// `info`/`pass` context.
//
// Plain JS so it has exactly one JS-side home: the api imports it directly to emit
// the `location_consistency` signal at verify time (api/routes/candidate.mjs), and
// the strict TS frontend re-exports it through src/lib/locationConsistency.ts
// (types live in locationConsistency.d.mts, like plans.mjs/plans.d.mts).
//
// The country-derivation tables and the agreement rule are the parity twin of the
// iOS `LocationConsistency` (LocationConsistency.swift) — keep the two in sync.

/**
 * Build the location-consistency check, or `null` when there isn't even one
 * location signal to show.
 *
 * @param {string|undefined} statedLocation   the candidate's free-text location (`Candidate.location`)
 * @param {string|undefined} phoneCountryCode `PhoneRisk.countryCode` (dialing code like "1", or ISO)
 * @param {{label: string, text: string}[]} [profileLocations] free-text locations the connected profiles list (GitHub)
 * @param {{country: string, region?: string}|undefined} [deviceRegion] the coarse device fix captured at verify time
 * @returns {{label: string, detail: string, state: 'pass'|'info'|'fail'}|null}
 */
export function locationConsistencyCheck(
  statedLocation,
  phoneCountryCode,
  profileLocations = [],
  deviceRegion = undefined,
  phoneRegion = undefined,
) {
  // Each source → (human label, candidate ISO countries, optional US state). A source
  // contributes to the country verdict only when we can derive a country from it; the
  // `region` (US state) is an optional second-pass refinement (see below).
  const sources = []

  // The device's own coarse region — an ISO country (already reverse-geocoded on
  // device), so no parsing needed. Listed first (mirrors LocationConsistency.swift).
  const deviceCountry = (deviceRegion?.country ?? '').trim().toUpperCase()
  if (deviceCountry) {
    const area = (deviceRegion?.region ?? '').trim()
    const label = area ? `device region (${deviceCountry} · ${area})` : `device region (${deviceCountry})`
    sources.push({ label, countries: new Set([deviceCountry]), region: usStateCode(area) })
  }

  const phone = countriesFromPhone(phoneCountryCode)
  if (phone && phone.size > 0) {
    // The phone's *line registration* region from PhoneID (e.g. "WA"), independent of
    // where the person says they are — a finer corroboration grain than country.
    const region = usStateCode(phoneRegion)
    const label = region ? `phone (${phoneCountryCode} · ${region})` : `phone (${phoneCountryCode})`
    sources.push({ label, countries: phone, region })
  }

  const statedRaw = (statedLocation ?? '').trim()
  const stated = countriesFromStated(statedRaw)
  if (stated && stated.size > 0 && statedRaw) {
    sources.push({ label: `stated (${statedRaw})`, countries: stated, region: usStateCode(statedRaw) })
  }

  // Profile-listed locations (e.g. GitHub) — an independent claim the candidate
  // didn't make on their Vouchr profile, so agreement is real corroboration.
  for (const profile of profileLocations) {
    const raw = (profile.text ?? '').trim()
    if (!raw) continue
    const parsed = countriesFromStated(raw)
    if (parsed && parsed.size > 0) {
      sources.push({ label: `${profile.label} (${raw})`, countries: parsed, region: usStateCode(raw) })
    }
  }

  if (sources.length === 0) return null

  // A single location signal can't corroborate anything — surface as context.
  if (sources.length < 2) {
    return {
      label: 'Location',
      detail: `One location signal on file: ${sources[0].label}. Add another (verify a phone, allow coarse location, connect a profile) to corroborate it.`,
      state: 'info',
    }
  }

  const list = sources.map((s) => s.label).join(' · ')
  // Consistent iff every pair of sources shares at least one possible country.
  const consistent = sources.every((a, i) =>
    sources.slice(i + 1).every((b) => [...a.countries].some((c) => b.countries.has(c))),
  )

  if (!consistent) {
    return {
      label: 'Location consistency',
      detail: `Location signals don't line up (${list}). Flagged for a human to review — location can be masked (VPN, travel), so this is never an auto-block.`,
      state: 'fail',
    }
  }

  // Country signals agree. Second pass — region cross-check among sources that carry a
  // confidently-derived US state (the phone's line registration, the device admin area,
  // an explicit "City, ST"). Matching regions are *stronger* corroboration; a divergence
  // (phone registers WA, stated NY) downgrades to `info` for a human glance — never
  // `fail`, since a recent move or a number kept from a previous city explains it.
  // Region layer is web/api-only: iOS PhoneRisk carries no line locale yet, so the
  // country tables + agreement rule above remain the parity twin of LocationConsistency.swift.
  const regions = sources.filter((s) => s.region)
  if (regions.length >= 2) {
    const distinct = new Set(regions.map((s) => s.region))
    if (distinct.size === 1) {
      return {
        label: 'Location consistency',
        detail: `Location signals agree, down to the same region (${[...distinct][0]}) — ${list}.`,
        state: 'pass',
      }
    }
    return {
      label: 'Location consistency',
      detail: `Location signals agree on country but differ on region (${regions
        .map((s) => s.label)
        .join(' vs ')}). Worth a human glance — a recent move or a number kept from a previous city can explain it; never an auto-block.`,
      state: 'info',
    }
  }

  return { label: 'Location consistency', detail: `Location signals agree (${list}).`, state: 'pass' }
}

// ── Country derivation (pure, conservative) ──────────────────────────────────

/** Map a phone `countryCode` to candidate ISO countries. Accepts an ISO alpha-2
 *  directly, or a numeric dialing code via a small table of the common ones.
 *  @param {string|undefined} code @returns {Set<string>|null} */
export function countriesFromPhone(code) {
  const raw = (code ?? '').trim()
  if (!raw) return null
  // ISO alpha-2 (e.g. "US", "gb").
  if (raw.length === 2 && /^[a-z]{2}$/i.test(raw)) return new Set([raw.toUpperCase()])
  // Numeric dialing code, tolerating a leading "+".
  const digits = raw.startsWith('+') ? raw.slice(1) : raw
  if (!/^\d+$/.test(digits)) return null
  return DIALING_CODE_TO_COUNTRIES[digits] ? new Set(DIALING_CODE_TO_COUNTRIES[digits]) : null
}

/** Best-effort country from free-text location. Conservative: returns a country
 *  only when confidently parseable (a US state token, an explicit US phrase, or a
 *  known country name/alias); otherwise `null` ("no claim", never a false conflict).
 *  @param {string|undefined} text @returns {Set<string>|null} */
export function countriesFromStated(text) {
  const raw = (text ?? '').trim()
  if (!raw) return null
  const lower = raw.toLowerCase()
  const tokens = new Set(lower.split(/[^a-z]+/i).filter(Boolean))

  // Explicit US phrases, a US state (via the conservative usStateCode parser), or
  // "us"/"usa" as a standalone token. Token-matching "us" avoids false hits inside
  // other words. CRITICAL (L16): use usStateCode on the ORIGINAL-case text — it
  // requires a bare 2-letter token to be UPPERCASE (or a full state name), so common
  // lowercase English words ("in", "or", "me" in "based in Berlin") no longer parse as
  // a US state and falsely flag a location conflict.
  if (
    lower.includes('united states') ||
    lower.includes('america') ||
    tokens.has('usa') ||
    tokens.has('us') ||
    usStateCode(raw) !== null
  ) {
    return new Set(['US'])
  }

  // Multi-word / long country names: substring is safe.
  for (const [name, iso] of LONG_COUNTRY_NAMES) if (lower.includes(name)) return new Set([iso])
  // Short aliases (uk, uae, …) must match as a standalone token.
  for (const [alias, iso] of SHORT_COUNTRY_ALIASES) if (tokens.has(alias)) return new Set([iso])
  return null
}

/** A confident US state code (2-letter, uppercase) from an admin-area or free-text
 *  string, or null. Conservative for free text: a 2-letter token must be UPPERCASE
 *  (so "Austin, TX" matches but the English words "in"/"or"/"me" don't); full state
 *  names match case-insensitively (qualified names like "West Virginia" listed before
 *  "Virginia" so the longer one wins). Used only for the region cross-check, never the
 *  country verdict — so a mis-parse can at most soften a `pass` to `info`, never block.
 *  @param {string|undefined} text @returns {string|null} */
export function usStateCode(text) {
  const raw = (text ?? '').trim()
  if (!raw) return null
  for (const t of raw.split(/[^A-Za-z]+/).filter(Boolean)) {
    if (t.length === 2 && t === t.toUpperCase() && US_STATE_TOKENS.has(t.toLowerCase())) return t.toUpperCase()
  }
  const lower = raw.toLowerCase()
  for (const [name, code] of US_STATE_NAMES) if (lower.includes(name)) return code
  return null
}

// ── Tables (mirror LocationConsistency.swift) ────────────────────────────────

const DIALING_CODE_TO_COUNTRIES = {
  '1': ['US', 'CA'], '7': ['RU', 'KZ'], '20': ['EG'], '27': ['ZA'],
  '31': ['NL'], '33': ['FR'], '34': ['ES'], '39': ['IT'], '44': ['GB'],
  '49': ['DE'], '52': ['MX'], '55': ['BR'], '61': ['AU'], '63': ['PH'],
  '64': ['NZ'], '65': ['SG'], '81': ['JP'], '82': ['KR'], '84': ['VN'],
  '86': ['CN'], '90': ['TR'], '91': ['IN'], '92': ['PK'], '234': ['NG'],
  '351': ['PT'], '353': ['IE'], '358': ['FI'], '380': ['UA'], '971': ['AE'],
  '972': ['IL'], '880': ['BD'], '62': ['ID'], '60': ['MY'], '66': ['TH'],
  '48': ['PL'], '46': ['SE'], '47': ['NO'], '45': ['DK'], '41': ['CH'],
  '43': ['AT'], '32': ['BE'], '30': ['GR'], '54': ['AR'], '56': ['CL'],
  '57': ['CO'], '51': ['PE'], '98': ['IR'], '212': ['MA'], '254': ['KE'],
}

const US_STATE_TOKENS = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id',
  'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms',
  'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok',
  'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv',
  'wi', 'wy', 'dc',
])

// Full US state names → 2-letter code, for usStateCode's region cross-check. Qualified
// multi-word names are listed BEFORE the names they contain ("west virginia" before
// "virginia", "south dakota" before any "dakota") so a substring scan picks the longer.
const US_STATE_NAMES = [
  ['west virginia', 'WV'], ['north carolina', 'NC'], ['south carolina', 'SC'],
  ['north dakota', 'ND'], ['south dakota', 'SD'], ['new hampshire', 'NH'],
  ['new jersey', 'NJ'], ['new mexico', 'NM'], ['new york', 'NY'], ['rhode island', 'RI'],
  ['district of columbia', 'DC'], ['washington', 'WA'], ['alabama', 'AL'], ['alaska', 'AK'],
  ['arizona', 'AZ'], ['arkansas', 'AR'], ['california', 'CA'], ['colorado', 'CO'],
  ['connecticut', 'CT'], ['delaware', 'DE'], ['florida', 'FL'], ['georgia', 'GA'],
  ['hawaii', 'HI'], ['idaho', 'ID'], ['illinois', 'IL'], ['indiana', 'IN'], ['iowa', 'IA'],
  ['kansas', 'KS'], ['kentucky', 'KY'], ['louisiana', 'LA'], ['maine', 'ME'],
  ['maryland', 'MD'], ['massachusetts', 'MA'], ['michigan', 'MI'], ['minnesota', 'MN'],
  ['mississippi', 'MS'], ['missouri', 'MO'], ['montana', 'MT'], ['nebraska', 'NE'],
  ['nevada', 'NV'], ['ohio', 'OH'], ['oklahoma', 'OK'], ['oregon', 'OR'],
  ['pennsylvania', 'PA'], ['tennessee', 'TN'], ['texas', 'TX'], ['utah', 'UT'],
  ['vermont', 'VT'], ['virginia', 'VA'], ['wisconsin', 'WI'], ['wyoming', 'WY'],
]

const LONG_COUNTRY_NAMES = [
  ['united kingdom', 'GB'], ['england', 'GB'], ['scotland', 'GB'], ['wales', 'GB'],
  ['canada', 'CA'], ['australia', 'AU'], ['germany', 'DE'], ['france', 'FR'],
  ['spain', 'ES'], ['italy', 'IT'], ['netherlands', 'NL'], ['ireland', 'IE'],
  ['india', 'IN'], ['pakistan', 'PK'], ['bangladesh', 'BD'], ['japan', 'JP'],
  ['china', 'CN'], ['singapore', 'SG'], ['philippines', 'PH'], ['vietnam', 'VN'],
  ['indonesia', 'ID'], ['malaysia', 'MY'], ['thailand', 'TH'], ['brazil', 'BR'],
  ['mexico', 'MX'], ['argentina', 'AR'], ['chile', 'CL'], ['colombia', 'CO'],
  ['nigeria', 'NG'], ['kenya', 'KE'], ['south africa', 'ZA'], ['egypt', 'EG'],
  ['poland', 'PL'], ['sweden', 'SE'], ['norway', 'NO'], ['denmark', 'DK'],
  ['switzerland', 'CH'], ['austria', 'AT'], ['belgium', 'BE'], ['portugal', 'PT'],
  ['turkey', 'TR'], ['ukraine', 'UA'], ['israel', 'IL'], ['new zealand', 'NZ'],
  ['united arab emirates', 'AE'], ['dubai', 'AE'], ['abu dhabi', 'AE'],
  ['south korea', 'KR'], ['korea', 'KR'],
]

const SHORT_COUNTRY_ALIASES = [
  ['uk', 'GB'], ['uae', 'AE'], ['nz', 'NZ'],
]
